import { observer } from 'mobx-react-lite';
import { ElementType, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { To, useNavigate, useParams } from 'react-router';
import { StepElement } from '../../../lib/components/compound/progress/stepper';
import { useStepper } from '../partials/actionStepperPartial';
import { Loading } from '../../../lib/components/atomic';
import { Description } from './steps/description';
import { TaxTreatment } from './steps/taxTreatment';
import { PayoutType } from './steps/payoutType';
import { EmployeeAssignment } from './steps/employeeAssignment';
import { Message } from './steps/message';
import { Summary } from './steps/summary';
import { useActions } from '../../../hooks/useActions';
import { useGetBonus } from '../../../queries/bonus';
import { useCreateBonus } from './hooks/useCreateBonus';

export interface StepDef {
  content: FC;
  title?: string | null;
  sectionTitle?: string | null;
  stepperConfig?: Array<Omit<StepElement, 'icon' & { icon?: ElementType }>>;
  nextText?: string;
  hideNavigation?: boolean;
}

type BonusProcess = 'create_bonus' | 'edit_bonus';

export const CreateBonus = observer(() => {
  const { t } = useTranslation();
  const { newBonus } = useCreateBonus();
  const params = useParams();
  const navigate = useNavigate();

  const description: StepDef = {
    content: Description,
    sectionTitle: t('bonus:titleAndDescription')
  };

  const taxTreatment: StepDef = {
    content: TaxTreatment,
    sectionTitle: t('bonus:taxTreatment')
  };

  const payoutType: StepDef = {
    content: PayoutType,
    sectionTitle: t('bonus:payoutType')
  };

  const employeeAssignment: StepDef = {
    content: EmployeeAssignment,
    sectionTitle: t('benefitSet:chooseEmployees')
  };

  const message: StepDef = {
    content: Message,
    sectionTitle: t('bonus:writeMessage')
  };

  const summary: StepDef = {
    content: Summary,
    sectionTitle: t('bonus:reviewAndConfirm')
  };

  const createBonusStepper = [
    {
      title: t('bonus:titleAndDescription')
    },
    {
      title: t('bonus:taxTreatment')
    },
    {
      title: t('bonus:payoutType')
    },
    {
      title: t('benefitSet:chooseEmployees')
    },
    {
      title: t('bonus:writeMessage')
    },
    {
      title: t('bonus:reviewAndConfirm')
    }
  ];

  const step: Record<BonusProcess, Array<StepDef>> = {
    create_bonus: [
      {
        ...description,
        title: t('bonus:create'),
        stepperConfig: createBonusStepper
      },
      taxTreatment,
      payoutType,
      employeeAssignment,
      message,
      summary
    ],
    edit_bonus: [
      {
        ...description,
        title: t('bonus:edit'),
        stepperConfig: createBonusStepper
      },
      taxTreatment,
      payoutType,
      employeeAssignment,
      message,
      summary
    ]
  };

  const {
    setTitle,
    setSectionTitle,
    setStepperConfig,
    setBackToPath,
    setBackToTitle,
    setShowHelpButton,
    setCustomCancelHandler,
    setHasChangedFields,
    setBackButtonConfig,
    setEditMode,
    setActiveStep
  } = useStepper();

  const { setSelectedEmployees } = useActions();

  const defaultStep = { ...step[params.type as BonusProcess][1], title: 'Bonus' };
  const [currentStep, setCurrentStep] = useState<StepDef>(defaultStep);

  const { data: bonusDataByUrl, isLoading: isLoadingBonus } = useGetBonus(params.id, {
    enabled: !!params.id && !newBonus.editingId
  });

  useEffect(() => {
    if (bonusDataByUrl && !newBonus.editingId) {
      newBonus.loadBonus(bonusDataByUrl);
    }
  }, [bonusDataByUrl, newBonus]);

  useEffect(() => {
    setCustomCancelHandler(() => (path: To) => navigate(path));

    return () => setCustomCancelHandler(null);
  }, [params.type, navigate]);

  // Clear only on unmount: while mounted, an empty store makes the redirect check redirect below think the data is missing, and it sends the user back to step 1.
  useEffect(
    () => () => {
      setSelectedEmployees({});
      newBonus.clear();
    },
    [newBonus, setSelectedEmployees]
  );

  useEffect(() => {
    if (newBonus.changedFields.length > 0) {
      setHasChangedFields(true);
    } else {
      setHasChangedFields(false);
    }
  }, [newBonus.changedFields.length]);

  useEffect(() => {
    setStepperConfig(createBonusStepper);
    setBackToTitle(t('custom:back_to_overview'));
    setBackToPath('/bonus');
    setBackButtonConfig({
      disabled: !!newBonus.editingId
    });
  }, [newBonus.editingId, currentStep]);

  useEffect(() => {
    setShowHelpButton(true);
  }, [currentStep]);

  useEffect(() => {
    if (newBonus.editingId) {
      setEditMode(true);
    }
  }, [newBonus.editingId]);

  useEffect(() => {
    if (params.type && !step[params.type as BonusProcess]) {
      navigate('/404');
    } else {
      const nextStep =
        params.type && params.step
          ? step[params.type as BonusProcess][+params.step - 1]
          : currentStep;
      setCurrentStep(nextStep);
      nextStep.title !== undefined && setTitle(nextStep.title ?? undefined);
      nextStep.sectionTitle !== undefined && setSectionTitle(nextStep.sectionTitle ?? undefined);
      nextStep.stepperConfig && setStepperConfig(nextStep.stepperConfig);
    }
  }, [params]);

  useEffect(() => {
    const isCreate = params.type === 'create_bonus';
    const isEdit = params.type === 'edit_bonus';
    const hasData = !!newBonus.title || !!newBonus.editingId || isLoadingBonus;

    if (!hasData && params.step !== '1') {
      if (isCreate) {
        setActiveStep(1);
      } else if (isEdit && !params.id) {
        // Only redirect edit flow back to overview if there's no ID in the URL to recover from
        navigate('/bonus');
      }
    }
  }, [
    navigate,
    newBonus.title,
    newBonus.editingId,
    params.type,
    params.step,
    params.id,
    setActiveStep,
    isLoadingBonus
  ]);

  if (isLoadingBonus) {
    return <Loading />;
  }

  const Step = currentStep.content;

  return <Step />;
});
